import React from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import AddRangeDialog from "./AddRangeDialog";
import AddRowDialog from "./AddRowDialog";
import DeleteRowDialog from "./DeleteRowDialog";
import ImportDialog from "./ImportDialog";
import TariffMatrixTable from "./TariffMatrixTable";
import { getBandValidationError } from "./structuralEditing";
import { useCarrierTariffMatrix } from "./useCarrierTariffMatrix";

const CrossDockingTariffs = () => {
  const toastRef = React.useRef(null);
  const [isAddRangeVisible, setIsAddRangeVisible] = React.useState(false);
  const [isAddRowVisible, setIsAddRowVisible] = React.useState(false);
  const [isDeleteVisible, setIsDeleteVisible] = React.useState(false);
  const [dialogError, setDialogError] = React.useState("");
  const [isStructuralLoading, setIsStructuralLoading] = React.useState(false);
  const [targetRow, setTargetRow] = React.useState(null);
  const [selectedRowIds, setSelectedRowIds] = React.useState(new Set());
  const [importOpen, setImportOpen] = React.useState(false);
  const [isBulkActivePending, setIsBulkActivePending] = React.useState(false);
  const {
    activeCarrier,
    bands,
    carrierGroups,
    createTariffBand,
    createTariffRow,
    deleteTariffRow,
    dirtyCount,
    isLoadingRegistry,
    isLoadingTariffs,
    isSaving,
    loadMissingAddressesForCarrier,
    loadRegistry,
    loadTariffsForCarrier,
    rows,
    saveDirtyRows,
    searchKato,
    updateCellDraft,
    startImportPreview,
    commitImport,
    setActiveBulk: bulkSetActive,
    importPreview,
    importStage,
    clearImportPreview,
  } = useCarrierTariffMatrix();

  const showMessage = React.useCallback((severity, summary, detail, life = 7000) => {
    toastRef.current?.show({ severity, summary, detail, life });
  }, []);

  React.useEffect(() => {
    loadRegistry().then((result) => {
      if (result?.usedMock) {
        showMessage("warn", "Показаны мок-данные", "Реестр кросс-докинга загружен из локального источника.");
      }
    });
  }, [loadRegistry, showMessage]);

  React.useEffect(() => {
    if (!activeCarrier) {
      return;
    }

    loadTariffsForCarrier(activeCarrier).then((result) => {
      if (result?.usedMock) {
        showMessage("warn", "Показаны мок-данные", `Тарифы для ${activeCarrier} загружены из локального источника.`);
      }
    });
    loadMissingAddressesForCarrier(activeCarrier);
  }, [activeCarrier, loadMissingAddressesForCarrier, loadTariffsForCarrier, showMessage]);

  React.useEffect(() => setSelectedRowIds(new Set()), [activeCarrier]);

  const onRefresh = React.useCallback(async () => {
    const result = await loadRegistry();
    if (result?.usedMock) {
      showMessage("warn", "Показаны мок-данные", "Не удалось обновить реестр, использованы локальные данные.");
    } else {
      showMessage("success", "Обновлено", "Реестр кросс-докинга обновлен.");
    }
  }, [loadRegistry, showMessage]);

  const onSave = React.useCallback(async () => {
    try {
      const result = await saveDirtyRows();

      if (!result.updated.length && !result.rejected.length) {
        showMessage("info", "Нет изменений", "Измененные тарифы не найдены.");
        return;
      }

      if (result.updated.length) {
        showMessage("success", "Сохранено", `Обновлено строк: ${result.updated.length}.`);
      }

      if (result.rejected.length) {
        showMessage("warn", "Есть отклонения", `Отклонено изменений: ${result.rejected.length}.`);
      }
    } catch (error) {
      showMessage("error", "Не удалось сохранить", "Сохранение тарифов завершилось ошибкой.");
    }
  }, [saveDirtyRows, showMessage]);

  const closeDialogs = React.useCallback(() => {
    setDialogError("");
    setIsAddRangeVisible(false);
    setIsAddRowVisible(false);
    setIsDeleteVisible(false);
  }, []);

  const onFileSelected = React.useCallback(async (file) => {
    try {
      await startImportPreview(file);
    } catch (error) {
      showMessage("error", "Ошибка", error.message);
    }
  }, [startImportPreview, showMessage]);

  const onCommit = React.useCallback(async (approvedNewBandKeys) => {
    try {
      const result = await commitImport({ approvedNewBandKeys });
      if (result.status === "ok") {
        showMessage("success", "Импорт применён", "Изменения применены.");
      } else if (result.status === "partial") {
        showMessage("warn", "Импорт применён частично",
          result.activeStateError || "Статус активности не был обновлён.");
      }
      setImportOpen(false);
    } catch (error) {
      showMessage("error", "Импорт не выполнен", error.message);
    }
  }, [commitImport, showMessage]);

  const onBulkActive = React.useCallback(async (nextActive, pointIds) => {
    setIsBulkActivePending(true);
    try {
      await bulkSetActive(pointIds.map((id) => {
        const numeric = typeof id === "string" ? Number(id.split(":")[0]) : id;
        return { crossDockingPointId: numeric, isActive: nextActive };
      }));
      showMessage("success", "Обновлено", `Изменено: ${pointIds.length}`);
    } catch (error) {
      showMessage("error", "Ошибка", error.message);
    } finally {
      setIsBulkActivePending(false);
    }
  }, [bulkSetActive, showMessage]);

  const onBulkActiveAll = React.useCallback(async (nextActive) => {
    const group = carrierGroups.find((g) => g.key === activeCarrier);
    if (!group) return;
    const pointIds = group.rows.map((r) => r.crossDockingPointId ?? r.id);
    const confirmed = window.confirm(
      `${nextActive ? "Включить" : "Отключить"} все ${pointIds.length} точек?`
    );
    if (!confirmed) return;
    await onBulkActive(nextActive, pointIds);
  }, [activeCarrier, carrierGroups, onBulkActive]);

  const onToggleActive = React.useCallback(async (row, nextActive) => {
    await onBulkActive(nextActive, [row.crossDockingPointId ?? row.id]);
  }, [onBulkActive]);

  const handleAddRange = React.useCallback(async (value, reset) => {
    const error = getBandValidationError(value, bands);
    if (error) {
      setDialogError(error);
      return;
    }

    setIsStructuralLoading(true);
    setDialogError("");
    try {
      await createTariffBand(value);
      reset();
      closeDialogs();
      showMessage("success", "Диапазон добавлен", "Новый весовой брейк создан.");
    } catch (errorMessage) {
      setDialogError(String(errorMessage || "Не удалось добавить диапазон."));
    } finally {
      setIsStructuralLoading(false);
    }
  }, [bands, closeDialogs, createTariffBand, showMessage]);

  const handleAddRow = React.useCallback(async (value, reset) => {
    if (!value) {
      setDialogError("Выберите КАТО.");
      return;
    }

    setIsStructuralLoading(true);
    setDialogError("");
    try {
      await createTariffRow({ katoOption: value });
      reset();
      closeDialogs();
      showMessage("success", "Строка добавлена", "Новая строка кросс-докинга создана.");
    } catch (errorMessage) {
      setDialogError(String(errorMessage || "Не удалось добавить строку."));
    } finally {
      setIsStructuralLoading(false);
    }
  }, [closeDialogs, createTariffRow, showMessage]);

  const handleDeleteRow = React.useCallback(async () => {
    if (!targetRow) {
      return;
    }

    setIsStructuralLoading(true);
    try {
      await deleteTariffRow(targetRow);
      setTargetRow(null);
      closeDialogs();
      showMessage("success", "Строка удалена", "Строка кросс-докинга удалена.");
    } catch (errorMessage) {
      showMessage("error", "Не удалось удалить строку", String(errorMessage || "Произошла ошибка."));
    } finally {
      setIsStructuralLoading(false);
    }
  }, [closeDialogs, deleteTariffRow, showMessage, targetRow]);

  return (
    <div>
      <Toast ref={toastRef} />
      <AddRangeDialog
        visible={isAddRangeVisible}
        onHide={closeDialogs}
        onSubmit={handleAddRange}
        loading={isStructuralLoading}
        error={dialogError}
      />
      <AddRowDialog
        visible={isAddRowVisible}
        onHide={closeDialogs}
        onSearch={searchKato}
        onSubmit={handleAddRow}
        loading={isStructuralLoading}
        error={dialogError}
      />
      <DeleteRowDialog
        visible={isDeleteVisible}
        row={targetRow}
        onHide={closeDialogs}
        onConfirm={handleDeleteRow}
        loading={isStructuralLoading}
      />
      <ImportDialog
        visible={importOpen}
        stage={importStage}
        preview={importPreview}
        onHide={() => { setImportOpen(false); clearImportPreview(); }}
        onFileSelected={onFileSelected}
        onCommit={onCommit}
      />

      <div className="p-d-flex p-jc-between p-ai-start p-flex-wrap" style={{ gap: "1rem" }}>
        <div>
          <h1 style={{ marginBottom: "0.35rem" }}>Тарифы кросс-докинга</h1>
        </div>

        <div className="p-d-flex p-ai-center p-flex-wrap" style={{ gap: "0.5rem" }}>
          <span style={{ color: "#667085", fontSize: "0.95rem" }}>
            Изменено строк: {dirtyCount}
          </span>
          <Button
            className="p-button-success p-button-sm"
            label="Сохранить изменения"
            icon="pi pi-save"
            onClick={onSave}
            disabled={!dirtyCount}
            loading={isSaving}
          />
          <Button
            className="p-button-sm"
            label="Добавить диапазон"
            icon="pi pi-plus"
            onClick={() => {
              setDialogError("");
              setIsAddRangeVisible(true);
            }}
            disabled={!activeCarrier}
          />
          <Button
            className="p-button-sm p-button-outlined"
            label="Добавить строку"
            icon="pi pi-plus-circle"
            onClick={() => {
              setDialogError("");
              setIsAddRowVisible(true);
            }}
            disabled={!activeCarrier}
          />
          <Button
            className="p-button-sm p-button-danger p-button-outlined"
            label="Удалить строку"
            icon="pi pi-trash"
            onClick={() => setIsDeleteVisible(true)}
            disabled={!targetRow}
          />
          <Button
            className="p-button-outlined p-button-sm"
            icon="pi pi-refresh"
            label="Обновить"
            onClick={onRefresh}
            loading={isLoadingRegistry}
          />
          <Button label="Импорт из файла" icon="pi pi-upload"
            onClick={() => setImportOpen(true)}
            disabled={!activeCarrier || dirtyCount > 0 || isSaving} />
          <Button label="Вкл. выбранные" icon="pi pi-check"
            disabled={selectedRowIds.size === 0 || isBulkActivePending}
            onClick={() => onBulkActive(true, [...selectedRowIds])} />
          <Button label="Откл. выбранные" icon="pi pi-ban" severity="secondary"
            disabled={selectedRowIds.size === 0 || isBulkActivePending}
            onClick={() => onBulkActive(false, [...selectedRowIds])} />
          <Button label="Вкл. все" icon="pi pi-check-circle" severity="info"
            disabled={!activeCarrier || isBulkActivePending}
            onClick={() => onBulkActiveAll(true)} />
          <Button label="Откл. все" icon="pi pi-times-circle" severity="warning"
            disabled={!activeCarrier || isBulkActivePending}
            onClick={() => onBulkActiveAll(false)} />
        </div>
      </div>

      <TariffMatrixTable
        rows={rows}
        bands={bands}
        loading={isLoadingRegistry || isLoadingTariffs}
        onPriceChange={updateCellDraft}
        onTargetRowChange={setTargetRow}
        selectedRowIds={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        onToggleActive={onToggleActive}
      />
    </div>
  );
};

export default CrossDockingTariffs;
